/**
 * Issue #1476 — which Supabase accounts are OURS.
 *
 * ── THE PROBLEM ──────────────────────────────────────────────────────────────
 *
 * Internal accounts are indistinguishable from real users in every ops surface,
 * and that has now caused two identity mix-ups in one week:
 *
 *   • Sep 2, 2026 — a task audit reported a "Pro user failure". It was the e2e
 *     fixture account, which signs in and fails things on purpose every CI run.
 *   • Sep 6, 2026 — Sentry triage read Kasandra's own Pro account as an
 *     aggrieved real mom and spent the session on it.
 *
 * Both cost real time, and both were unfalsifiable from the surface itself: a
 * Sentry event carries a bare uuid (`sentryUser.ts` deliberately sends the id
 * and nothing else), so there is no way to tell the founder from a customer
 * without going to the database and back.
 *
 * ── WHAT THIS IS AND IS NOT ──────────────────────────────────────────────────
 *
 * A LABEL, never a filter. Internal events still reach Sentry in full — triage
 * filters on the tag in the Sentry UI when it wants to. Suppressing them would
 * mean the founder's own crashes stop being reported, which is exactly backwards:
 * she is the one person guaranteed to be running the newest build.
 *
 * This is also NOT the same list as `supabase/functions/daily-digest/
 * internalAccounts.ts`, and the two must not be merged. That one answers "who
 * should not count as a signup" by EMAIL SHAPE, and it deliberately leaves
 * `kdchambers3@gmail.com` in the metrics — whether the founder counts in her own
 * signup numbers is a judgement call recorded there. This one answers "whose
 * error is not a customer's error" by ID, and for that question she is
 * unambiguously internal. Same word, two different questions.
 *
 * ── THE ID LIST IS MIRRORED IN SQL ───────────────────────────────────────────
 *
 * `public.internal_user_ids` (supabase/migrations/20260906234500_internal_user_ids_view.sql)
 * holds the identical list so audit and analytics queries can say
 * `where user_id not in (select user_id from internal_user_ids)`.
 * `src/lib/__tests__/internalAccounts.test.ts` fails if the two ever drift.
 */

/**
 * Every account we know is ours, ascending so the SQL mirror has one obvious
 * order to match.
 *
 * ⚠️ Add ids here, never to a query. Anything read straight out of this array
 * lands in both the Sentry tag and (via the drift test) the SQL view.
 */
export const INTERNAL_USER_IDS: readonly string[] = [
  // applereview@kinyoudoitall.com — the App Store reviewer account (#1504).
  //
  // ⚠️ THE ONE ENTRY WITH A COST, and it was chosen deliberately. Triage
  // filters `!internal:true`, so a crash DURING App Store review now sits
  // behind that filter — the one internal account whose errors are arguably
  // more urgent than a customer's, not less. Kasandra made this call on
  // 2026-09-08 with the tradeoff stated. Before a submission, search Sentry
  // for `internal:true` rather than filtering it out; this id is what to look
  // for. Do not "tidy" this account out of the list without asking her.
  "188242f6-bc28-462d-90c3-5811ae8c55be",
  // playwright-pro@kinyoudoitall.com — the comp-Pro e2e fixture
  // (`TEST_PRO_USER_EMAIL`), referenced by email in
  // 20260804040000_revoke_e2e_account_admin.sql. 28,502 sessions on 2026-09-08,
  // every one of them counting as a real user until this landed (#1504).
  "5140172e-ef7d-473b-9278-53eea8088e9f",
  // kinyoudoitall+playwright@gmail.com — the shared Playwright e2e fixture
  // (`TEST_USER_EMAIL`). Signs in on every CI run and deliberately drives
  // failure paths, so its errors are noise by construction. Sourced from
  // supabase/functions/e2e-seed-scheduled-run/index.ts and the three
  // e2e-fixture migrations.
  "96b145a6-0ada-4a2d-b44a-7a4998abc5c1",
  // kinyoudoitall+team@gmail.com — Kasandra's marketing team account (#1504).
  // This is what the #1476 TODO was reaching for as "Debbie's test account";
  // no account exists under that name, and this is the one she meant.
  "c1b2c7c9-9a80-4747-ba4f-45b3195a1938",
  // kinyoudoitall@gmail.com — Kasandra's owner/admin account (`is_admin = true`).
  "d791071c-3ed4-41d1-af85-2b2568659142",
  // kdchambers3@gmail.com — Kasandra's real day-to-day account, `tier = 'pro'`.
  // This is the one Sep 6 triage mistook for a customer: it looks like an
  // ordinary Pro mom in every surface, because she uses it like one.
  //
  // ⚠️ This comment used to state flatly that the account held no admin flag, and
  // that was FALSE when measured on 2026-09-26 — #1719's audit found
  // `is_admin = true` here, and the same false reassurance was sitting in
  // CLAUDE.md (corrected in #1731). Two copies of one unverified claim, in the two
  // files a reader checks first. Described rather than quoted, deliberately: the
  // original wording is greppable, and the next person searching for it should not
  // land on a post-mortem that reads like the live answer.
  //
  // #1730 revoked it and Kasandra reports running the SQL on 2026-09-26, but the
  // `RETURNING` output was not pasted back, so the state is recorded as REPORTED
  // rather than confirmed — see supabase/migrations/20260926130000_*.sql. The
  // admin table in CLAUDE.md carries the audit query that settles it.
  //
  // Nothing here depends on the answer: this list answers "whose error is not a
  // customer's error", and she is internal either way.
  "ec4e9a6d-c174-43d4-8abc-a7238f57776b",
  // ⚠️ STILL MATCHED, NOT COMPLETE — and no test can change that.
  // The verify query that sourced the three ids above only sees addresses
  // matching `%@kinyoudoitall.com` or `kinyoudoitall+%`. An internal account on
  // any other domain is still counting as a real user and nothing here will
  // say so. Closing THAT gap means reading `auth.users`, which robots do not do
  // (Critical Pipeline Rule 2).
  //
  // Deliberately NOT here: 064ffded-99dd-428d-a407-1207e933a26d, Kasandra's
  // pre-cutover Lovable Cloud id. It is hers, but that account cannot sign in
  // to Personal Supabase, so it can never produce a Sentry event or an
  // `auth.users` row here. Listing a uuid that matches zero rows would make the
  // view look more complete than it is (CLAUDE.md, ADMIN / OPERATIONAL FACTS).
];

/**
 * Lower-cased lookup set. Postgres hands back lower-case uuids and so does
 * supabase-js, but a case-folded compare costs nothing and a missed match here
 * fails in the silent direction — the account just quietly reads as a customer
 * again, which is the whole bug.
 */
const INTERNAL_ID_SET: ReadonlySet<string> = new Set(
  INTERNAL_USER_IDS.map((id) => id.toLowerCase()),
);

/**
 * Is this one of our own accounts?
 *
 * Returns `false` for null/undefined/blank rather than throwing: the caller is a
 * Sentry tag on a signed-out session, and "we have no id" is not "this is us".
 */
export function isInternalUserId(userId: string | null | undefined): boolean {
  if (!userId) return false;
  const trimmed = userId.trim().toLowerCase();
  if (!trimmed) return false;
  return INTERNAL_ID_SET.has(trimmed);
}
